import React, { useState } from "react";
import { Lock, Eye, EyeOff, ShieldCheck, LogOut } from "lucide-react";
import api from "../api";
import { K } from "../theme";

// Blocking screen shown when users.must_change_password is TRUE.
// Rendered by App.js between login and the main app — no sidebar,
// no routing, no way past it except changing the password (or
// signing out). Fires on:
//   - first login after admin creates an account (default password)
//   - first login after admin resets the account (even a custom
//     admin-supplied password is considered burnt because the admin
//     saw it in plaintext)
export const ForcePasswordChange = ({ username, onDone, onLogout }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const validate = () => {
    if (!cur || !next || !conf) return "Please fill every field.";
    if (next.length < 8) return "New password must be at least 8 characters.";
    if (!/[A-Za-z]/.test(next) || !/\d/.test(next))
      return "New password must include at least one letter and one number.";
    if (cur === next) return "New password must be different from the one you signed in with.";
    if (next !== conf) return "New password and confirmation don't match.";
    return null;
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const v = validate();
    if (v) { setErr(v); return; }
    setBusy(true); setErr("");
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: next });
      onDone();
    } catch (er) {
      setErr(er.message || "Change failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[420px] bg-bg1 border border-b0 rounded-2xl p-6 shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
            <ShieldCheck size={18} color="#fff" />
          </div>
          <div>
            <div className="text-[17px] font-extrabold text-t0">Set a new password</div>
            <div className="text-[14px] text-t3">Signed in as <span className="font-mono">{username || 'you'}</span></div>
          </div>
        </div>

        <div className="text-[14px] text-t2 leading-relaxed mb-4 bg-ab border border-ad rounded-lg p-3">
          You're using a temporary password issued by HR. For your account's safety, please choose a new one before continuing — this only takes a moment and cannot be skipped.
        </div>

        <form onSubmit={submit}>
          <div className="mb-3">
            <label className="block text-[14px] font-semibold text-t2 mb-[6px]">Current (temporary) password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3" />
              <input type={showCur ? "text" : "password"} value={cur} onChange={e => setCur(e.target.value)}
                     autoComplete="current-password" autoFocus
                     className="w-full pl-[36px] pr-[40px] py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-[#2563EB]" />
              <button type="button" onClick={() => setShowCur(v => !v)} aria-label={showCur ? 'Hide' : 'Show'}
                      className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {showCur ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          <div className="mb-3">
            <label className="block text-[14px] font-semibold text-t2 mb-[6px]">New password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3" />
              <input type={showNext ? "text" : "password"} value={next} onChange={e => setNext(e.target.value)}
                     autoComplete="new-password"
                     className="w-full pl-[36px] pr-[40px] py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-[#2563EB]" />
              <button type="button" onClick={() => setShowNext(v => !v)} aria-label={showNext ? 'Hide' : 'Show'}
                      className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {showNext ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <div className="text-[14px] text-t3 mt-[4px]">At least 8 characters, includes a letter and a number.</div>
          </div>

          <div className="mb-4">
            <label className="block text-[14px] font-semibold text-t2 mb-[6px]">Confirm new password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3" />
              <input type={showConf ? "text" : "password"} value={conf} onChange={e => setConf(e.target.value)}
                     autoComplete="new-password"
                     className="w-full pl-[36px] pr-[40px] py-[10px] bg-bg2 border border-b0 rounded-xl text-t0 text-[15px] outline-none focus:border-[#2563EB]" />
              <button type="button" onClick={() => setShowConf(v => !v)} aria-label={showConf ? 'Hide' : 'Show'}
                      className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-1">
                {showConf ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {err && (
            <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>
          )}

          <button type="submit" disabled={busy}
                  className="w-full flex items-center justify-center gap-2 py-[11px] rounded-xl border-0 cursor-pointer text-white text-[15px] font-bold disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
            {busy ? "Saving…" : "Update password & continue"}
          </button>

          <button type="button" onClick={onLogout}
                  className="w-full mt-2 flex items-center justify-center gap-2 py-[9px] rounded-xl border border-b0 bg-transparent text-t2 text-[14px] cursor-pointer">
            <LogOut size={12} /> Sign out
          </button>
        </form>
      </div>
    </div>
  );
};
